import { Helmet } from "react-helmet-async";
import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  Container,
  Typography,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { AutoGraph } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import CustomTable from "../components/data-table/CustomDataTable";
import Protected from "../protected/Protected";
import { selectUser } from "../redux/slices/auth.slice";
import {
  useGetApprovedSponsoredUsersQuery,
  useStopSponsoredSubscriptionsBulkMutation,
  useStopSponsoredSubscriptionMutation,
} from "../redux/services/sponsorship.service";
import UserUsageDialog from "../sections/@dashboard/organization-sponsorship/UserUsageDialog";

export default function SponsoredUserUsagePage() {
  const user = useSelector(selectUser);
  const roles = user?.roles?.map((r) => r.name) || [];
  const isOrganization = roles.includes("organization");
  const isSupport = roles.includes("support");
  const isOrgOrSupport = isOrganization || isSupport;

  const { enqueueSnackbar } = useSnackbar();
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmBulkOpen, setConfirmBulkOpen] = useState(false);
  const [bulkSelectedRows, setBulkSelectedRows] = useState([]);

  const [stopSubscriptionsBulk, { isLoading: isStoppingBulk }] = useStopSponsoredSubscriptionsBulkMutation();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [userToStop, setUserToStop] = useState(null);
  const [stopSubscription, { isLoading: isStopping }] = useStopSponsoredSubscriptionMutation();

  const handleOpenStopConfirm = (row) => {
    setUserToStop({
      id: row.userId,
      name: row.name,
    });
    setConfirmOpen(true);
  };

  const confirmStopSubscription = async () => {
    try {
      const res = await stopSubscription(userToStop.id).unwrap();
      if (res.success) {
        enqueueSnackbar(res.message || "Sponsored subscription stopped successfully.", { variant: "success" });
      } else {
        enqueueSnackbar(res.message || "Failed to stop subscription.", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar(err?.data?.message || "An error occurred while stopping subscription.", { variant: "error" });
    } finally {
      setConfirmOpen(false);
      setUserToStop(null);
    }
  };

  const handleOpenUsage = (row) => {
    // We set the user object with the real app userId to fetch their specific usage details
    setSelectedUser({
      id: row.userId, // users.id (real app user id)
      name: row.name,
      email: row.email,
      contactNumber: row.contactNumber,
      city: row.city || "",
      state: row.state || "",
      country: row.country || "",
      organizationName: row.organizationName,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleBulkStopSubscription = (selectedRows) => {
    setBulkSelectedRows(selectedRows);
    setConfirmBulkOpen(true);
  };

  const confirmBulkStopSubscription = async () => {
    const userIds = bulkSelectedRows.map((row) => row.userId).filter(Boolean);
    if (!userIds.length) {
      setConfirmBulkOpen(false);
      return;
    }

    try {
      const res = await stopSubscriptionsBulk(userIds).unwrap();
      if (res.success) {
        enqueueSnackbar(res.message || "Sponsored subscriptions stopped successfully.", { variant: "success" });
      } else {
        enqueueSnackbar(res.message || "Failed to stop subscriptions.", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar(err?.data?.message || "An error occurred while stopping subscriptions.", { variant: "error" });
    } finally {
      setConfirmBulkOpen(false);
    }
  };

  const orgOrSupportUsageColumns = useMemo(
    () => [
      {
        field: "userId",
        headerName: "User ID",
        defaultVisible: true,
        isViewable: true,
        isSortable: true,
        isFilterable: true,
        isDownloadable: true,
        type: "string",
        valueGetter: (value, row) => row?.userId || row?.id || "--",
      },
      ...(!isOrganization
        ? [
            {
              field: "organizationName",
              headerName: "Organization",
              defaultVisible: true,
              isViewable: true,
              isSortable: true,
              isFilterable: true,
              isDownloadable: true,
              type: "string",
            },
          ]
        : []),
      {
        field: "approvedAt",
        headerName: "Approved Date",
        defaultVisible: true,
        isViewable: true,
        isSortable: true,
        isFilterable: true,
        isDownloadable: true,
        type: "date",
        valueGetter: (value) => (value ? new Date(value) : null),
      },
    ],
    [isOrganization]
  );

  const actions = useMemo(
    () => ({
      field: "actions",
      headerName: "Actions",
      width: isOrganization ? 300 : 160,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} sx={{ height: "100%", alignItems: "center" }}>
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={<AutoGraph />}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenUsage(params.row);
            }}
          >
            Check Usage
          </Button>
          {isOrganization && (
            <Button
              variant="contained"
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenStopConfirm(params.row);
              }}
            >
              Stop Subscription
            </Button>
          )}
        </Stack>
      ),
    }),
    [isOrganization]
  );

  return (
    <Protected>
      <Helmet>
        <title>Sponsored User Usage | Smarton Dashboard</title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            Sponsored User Usage
          </Typography>
        </Stack>

        <Card>
          <CustomTable
            canSelect
            showBulkStopSubscription={isOrganization}
            onBulkStopSubscription={handleBulkStopSubscription}
            actions={actions}
            columnsOverride={isOrgOrSupport ? orgOrSupportUsageColumns : undefined}
            useDataQuery={useGetApprovedSponsoredUsersQuery}
            searchLabel={isOrgOrSupport ? "Search by User ID..." : "Search Approved User..."}
            tableName="SponsoredUserUsage"
            restrictDownloadCols={[]}
            searchCols={isOrgOrSupport ? ["userId", "id"] : ["name", "email", "contactNumber"]}
          />
        </Card>
      </Container>

      {selectedUser && (
        <UserUsageDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          user={selectedUser}
        />
      )}

      {/* Bulk Stop Confirmation Dialog */}
      <Dialog
        open={confirmBulkOpen}
        onClose={() => setConfirmBulkOpen(false)}
      >
        <DialogTitle>Stop Bulk Sponsored Subscriptions</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ pt: 1 }}>
            Are you sure you want to stop sponsored subscriptions for the <strong>{bulkSelectedRows.length}</strong> selected users? They will retain their current active premium month, and future months will not be automatically sponsored.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmBulkOpen(false)} color="inherit">
            Cancel
          </Button>
          <LoadingButton
            onClick={confirmBulkStopSubscription}
            color="error"
            variant="contained"
            loading={isStoppingBulk}
          >
            Stop Subscriptions
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Single Stop Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
      >
        <DialogTitle>Stop Sponsored Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ pt: 1 }}>
            Are you sure you want to stop the recurring sponsored subscription for <strong>{isOrgOrSupport ? (userToStop?.id || "this user") : (userToStop?.name || "this user")}</strong>? The user will keep their current active premium month until it expires, and no future months will be automatically sponsored.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} color="inherit">
            Cancel
          </Button>
          <LoadingButton
            onClick={confirmStopSubscription}
            color="error"
            variant="contained"
            loading={isStopping}
          >
            Stop Subscription
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Protected>
  );
}
